import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  useRepackingDetail,
  useRecordPackedGood,
  useCompleteRepacking,
} from '@hooks/useRepacking'

const packedGoodSchema = z.object({
  packSizeId: z.coerce.number().positive('Pack size is required'),
  qtyPacked: z.coerce.number().positive('Quantity must be greater than 0'),
})

type PackedGoodForm = z.infer<typeof packedGoodSchema>

export function RepackingDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useRepackingDetail(id ? parseInt(id) : undefined)
  const recordPacked = useRecordPackedGood()
  const complete = useCompleteRepacking()

  const repacking = data?.data

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<PackedGoodForm>({
    defaultValues: {
      packSizeId: 0,
      qtyPacked: 0,
    },
  })

  const handleRecordPacked = async (formData: PackedGoodForm) => {
    try {
      setError(null)
      await recordPacked.mutateAsync({
        repackingId: parseInt(id!),
        packSizeId: formData.packSizeId,
        qtyPacked: formData.qtyPacked,
      })
      reset()
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'Failed to record packed goods'
      )
    }
  }

  const handleComplete = async () => {
    if (
      window.confirm(
        'Mark this repacking job as complete? This will update inventory.'
      )
    ) {
      try {
        setError(null)
        await complete.mutateAsync(parseInt(id!))
        navigate('/dashboard/repacking')
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to complete repacking')
      }
    }
  }

  if (isLoading)
    return <div className="text-center py-8">Loading repacking details...</div>
  if (!repacking)
    return <div className="text-center py-8">Repacking job not found</div>

  const remainingYield = repacking.expectedYield - repacking.actualOutput
  const progress = (repacking.actualOutput / repacking.expectedYield) * 100

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {repacking.bulkBatch.product.name} - {repacking.bulkBatch.batchNo}
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Repacking by {repacking.repackerUser.fullName}
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard/repacking')}
          className="btn-secondary"
        >
          ← Back
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Input Quantity</div>
          <div className="text-2xl font-bold mt-1">
            {repacking.inputQty.toFixed(2)} {repacking.bulkBatch.product.baseUom}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Expected Yield</div>
          <div className="text-2xl font-bold mt-1">
            {repacking.expectedYield.toFixed(2)} {repacking.bulkBatch.product.baseUom}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Actual Output</div>
          <div className="text-2xl font-bold mt-1 text-blue-600">
            {repacking.actualOutput.toFixed(2)} {repacking.bulkBatch.product.baseUom}
          </div>
        </div>

        <div className="card">
          <div className="text-sm text-gray-600">Remaining</div>
          <div className="text-2xl font-bold mt-1 text-orange-600">
            {remainingYield.toFixed(2)} {repacking.bulkBatch.product.baseUom}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Progress</h2>
        </div>

        <div className="mb-4">
          <div className="flex justify-between mb-2">
            <span className="text-sm font-medium">Output Progress</span>
            <span className="text-sm font-medium">{progress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>

        {repacking.status === 'COMPLETED' && (
          <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
            <p className="font-medium text-green-900">
              Total Loss: {repacking.actualLoss?.toFixed(2)} {repacking.bulkBatch.product.baseUom} ({repacking.lossPercentage?.toFixed(1)}%)
            </p>
            <p className="text-green-700 text-xs mt-1">
              Expected loss: {repacking.expectedLoss?.toFixed(2)} {repacking.bulkBatch.product.baseUom} ({repacking.bulkBatch.product.expectedLossPct}%)
            </p>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Record Packed Goods</h2>
        </div>

        {repacking.status === 'COMPLETED' ? (
          <p className="text-gray-600 text-sm">
            This repacking job is completed. No more items can be added.
          </p>
        ) : (
          <form onSubmit={handleSubmit(handleRecordPacked)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pack Size *
                </label>
                <select {...register('packSizeId')} className="w-full">
                  <option value="">Select Pack Size</option>
                  {repacking.bulkBatch.product.packSizes.map((ps) => (
                    <option key={ps.id} value={ps.id}>
                      {ps.size} {ps.uom}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity Packed *
                </label>
                <input
                  {...register('qtyPacked', { valueAsNumber: true })}
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Number of packets"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="submit"
                className="btn-primary disabled:opacity-50"
                disabled={isSubmitting || recordPacked.isPending}
              >
                {isSubmitting || recordPacked.isPending
                  ? 'Recording...'
                  : 'Record Packed Good'}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Packed Goods</h2>
        </div>

        {!repacking.packedGoods || repacking.packedGoods.length === 0 ? (
          <p className="text-gray-600 text-sm">No packed goods recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-2 font-semibold">Pack Size</th>
                  <th className="text-right px-4 py-2 font-semibold">Quantity</th>
                  <th className="text-left px-4 py-2 font-semibold">Batch No.</th>
                </tr>
              </thead>
              <tbody>
                {repacking.packedGoods.map((pg) => (
                  <tr key={pg.id} className="border-b">
                    <td className="px-4 py-3">
                      {pg.packSize.size} {pg.packSize.uom}
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {pg.qtyPacked}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{pg.batchNo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {repacking.status !== 'COMPLETED' && (
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleComplete}
            className="btn-success disabled:opacity-50"
            disabled={complete.isPending}
          >
            {complete.isPending ? 'Completing...' : 'Complete Repacking Job'}
          </button>
        </div>
      )}
    </div>
  )
}
